import { Grid } from '@mui/material';
import { memo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { PmiButton } from 'shell/components/shared/button';
import { AccordionFamilyCoexistence as AccordionFamilyCoexistenceVM } from 'shell/view-models/accordion-family-coexistence';
import ParenthoodRelationshipList from 'shell/components/shared/parenthood-relationship-list';
import CohabitationBondList from 'shell/components/shared/cohabitation-bond-list';
import { FormikErrors, FormikTouched } from 'formik';
import PmiDatePicker from 'shell/components/shared/date-picker';

interface FamilyCoexistenceProps {
  disabled?: boolean;
  disabledFromAct?: boolean;
  isCohabitation?: boolean;
  values?: AccordionFamilyCoexistenceVM;
  errors?: FormikErrors<AccordionFamilyCoexistenceVM>;
  touched?: FormikTouched<AccordionFamilyCoexistenceVM>;
  onChange: (
    key: keyof AccordionFamilyCoexistenceVM,
    value: AccordionFamilyCoexistenceVM[keyof AccordionFamilyCoexistenceVM]
  ) => void;
  resetForm: () => void;
}

const FamilyCoexistenceFormComponent = ({
  disabled,
  disabledFromAct,
  values,
  errors,
  touched,
  onChange,
  resetForm,
  isCohabitation,
}: FamilyCoexistenceProps): React.ReactElement => {
  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Identificativo comune"
          aria-label="Identificativo comune"
          id="municipality-identifier"
          fullWidth
          onChange={(event) => {
            onChange('municipalityIdentifier', event?.target.value);
          }}
          value={values?.municipalityIdentifier || ''}
          disabled
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Codice ISTAT"
          aria-label="Codice ISTAT"
          id="istat-code"
          fullWidth
          onChange={(event) => {
            onChange('codeIstat', event?.target.value);
          }}
          value={values?.codeIstat || ''}
          disabled
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Identificativo ANPR"
          aria-label="Identificativo ANPR"
          id="identifier-anpr"
          fullWidth
          onChange={(event) => {
            onChange('identifierAnpr', event?.target.value);
          }}
          value={values?.identifierAnpr || ''}
          disabled
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data origine"
          value={values?.originDate || null}
          disabled
          onChange={(value) => {
            onChange('originDate', value || null);
          }}
          renderInput={(params) => (
            <PmiTextField {...params} id="originDate" fullWidth error={false} helperText="" />
          )}
        />
      </Grid>
      {isCohabitation ? (
        <Grid
          container
          item
          spacing={0}
          rowSpacing={{ xs: 1, md: 0 }}
          columnSpacing={{ xs: 0, md: 1 }}
          xs={12}
        >
          <Grid item xs={12} md={4}>
            <PmiTextField
              label="Specie convivenza"
              aria-label="Specie convivenza"
              id="special-cohabitation"
              fullWidth
              onChange={(event) => {
                onChange('especiallyCoexistenceDescription', event?.target.value);
              }}
              value={values?.especiallyCoexistenceDescription || ''}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <PmiTextField
              label="Denominazione convivenza"
              aria-label="Denominazione convivenza"
              id="name-coexistence"
              fullWidth
              onChange={(event) => {
                onChange('nameCoexistence', event?.target.value);
              }}
              value={values?.nameCoexistence || ''}
              disabled
            />
          </Grid>
        </Grid>
      ) : undefined}
      <Grid item xs={12} md={4}>
        <PmiDatePicker
          label="Data decorrenza appartenenza *"
          value={values?.membershipDate || null}
          disabled={disabled || values?.disabledDate || disabledFromAct}
          onChange={(value) => {
            onChange('membershipDate', value || null);
          }}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="membershipDate"
              fullWidth
              error={touched?.membershipDate && Boolean(errors?.membershipDate)}
              helperText={errors?.membershipDate}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data decorrenza legame *"
          value={values?.bondDate || null}
          disabled={disabled || values?.disabledDate || disabledFromAct}
          onChange={(value) => {
            onChange('bondDate', value || null);
          }}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="bondDate"
              fullWidth
              error={touched?.bondDate && Boolean(errors?.bondDate)}
              helperText={errors?.bondDate}
            />
          )}
        />
      </Grid>
      {!isCohabitation ? (
        <Grid
          container
          item
          spacing={0}
          rowSpacing={{ xs: 1, md: 0 }}
          columnSpacing={{ xs: 0, md: 1 }}
          xs={12}
        >
          <Grid item xs={12} md={5}>
            <ParenthoodRelationshipList
              label="Parentela"
              aria-label="Parentela"
              required
              id="relationship"
              onChange={(value) => {
                onChange('relationship', value);
              }}
              value={values?.relationship || ''}
              error={touched?.relationship && Boolean(errors?.relationship)}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <PmiTextField
              label="Prog. componente"
              aria-label="Prog. componente"
              id="prog-component"
              fullWidth
              onChange={(event) => {
                onChange('progComponent', event?.target.value);
              }}
              value={values?.progComponent || ''}
              error={touched?.progComponent && Boolean(errors?.progComponent)}
              helperText={errors?.progComponent}
              disabled={disabled}
              inputProps={{
                maxLength: '6',
              }}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid
          container
          item
          spacing={0}
          rowSpacing={{ xs: 1, md: 0 }}
          columnSpacing={{ xs: 0, md: 1 }}
          xs={12}
        >
          <Grid item xs={12} md={4}>
            <CohabitationBondList
              label="Legame convivenza *"
              aria-label="Legame convivenza"
              id="coexistence-bond"
              onChange={(value) => {
                onChange('coexistenceBond', value);
              }}
              value={values?.coexistenceBond || ''}
              error={touched?.coexistenceBond && Boolean(errors?.coexistenceBond)}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      )}
      <Grid container item spacing={2} xs={12}>
        <Grid item xs={12} md={2}>
          <PmiButton color="cdmBrandThird" variant="outlined" fullWidth onClick={resetForm}>
            Ripulisci
          </PmiButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

FamilyCoexistenceFormComponent.displayName = 'Family Coexistence Form component';

const FamilyCoexistenceForm = memo(FamilyCoexistenceFormComponent);

export default FamilyCoexistenceForm;
